import { createSlice, original, PayloadAction } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { isEqual } from "lodash";

import {
  getUserFromStorage,
  isUserAuthenticated,
  LocalStorageName,
} from "@/redux/api.js";
import {
  AuthenticationState,
  SetAuthenticatedPayload,
} from "@/redux/authentication/types";
import { FullUserResponse } from "@/redux/users/types";

const initialState: AuthenticationState = {
  loading: false,
  isAuthenticated: isUserAuthenticated(),
  user: getUserFromStorage(),
  isFirstLogin: false,
};

const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    setAuthenticationState: (
      state,
      { payload }: PayloadAction<SetAuthenticatedPayload>,
    ) => {
      const sentryUser =
        payload.isAuthenticated && payload.user
          ? {
              id: payload.user.id,
              email: payload.user.email as string,
              username: payload.user.fullName as string,
            }
          : null;

      Sentry.setUser(sentryUser);
      state.isAuthenticated = payload.isAuthenticated;
      state.user = payload?.user || null;
      state.isFirstLogin = payload?.isFirstLogin || false;
    },
    setUser: (state, { payload }: PayloadAction<FullUserResponse>) => {
      const previousUser = original(state)?.user;
      if (previousUser && isEqual(previousUser, payload)) return;

      state.user = payload;
      try {
        const stored = window.localStorage.getItem(LocalStorageName);
        if (stored) {
          const parsedData = JSON.parse(stored);
          parsedData.user = payload;
          window.localStorage.setItem(
            LocalStorageName,
            JSON.stringify(parsedData),
          );
        }
      } catch {
        // localStorage sync is best-effort
      }
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isFirstLogin = false;
      Sentry.setUser(null);
    },
    setIsFirstLogin: (state, { payload }: PayloadAction<boolean>) => {
      state.isFirstLogin = payload;
    },
  },
});

export const { setAuthenticationState, setUser, logout, setIsFirstLogin } =
  authenticationSlice.actions;
export default authenticationSlice.reducer;
